import React from 'react';
import { Target, Eye, CheckCircle2, Users, Award, Building, Clock, Globe, Wrench, Factory, ShieldCheck, Handshake, ArrowRight } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

type AboutPageProps = {
  onNavigate?: (page: string) => void;
};

export const AboutPage: React.FC<AboutPageProps> = ({ onNavigate }) => {
  const { isDark } = useTheme();

  const visionGoals = [
    'To continuously improve all the processes using reliable, thoroughly tested technology.',
    'To train and develop individuals with root cause analyzing skills, team working skills, and exceptional personnel competence to achieve the best results.',
    'To focus on mutually beneficial relationships with our customers and suppliers.',
    'To carry organizational values down the line.',
  ];

  const partnerReasons = [
    {
      icon: Clock,
      title: '45+ Years of Experience',
      description: 'Since 1979, we\'ve mastered precision heavy engineering',
    },
    {
      icon: ShieldCheck,
      title: 'ISO-Certified Quality',
      description: 'ISO 9001, 14001, 45001, 3834-2 certified operations',
    },
    {
      icon: Award,
      title: 'OEM Approved Partner',
      description: 'Trusted by Siemens Gamesa, FLSmidth, SMS Group, BHEL, NTPC, JSW Steel',
    },
    {
      icon: Globe,
      title: 'Export to 5+ Countries',
      description: 'Serving global markets across Asia, Middle East, America, Australia and Europe',
    },
    {
      icon: Factory,
      title: 'State of the Art Facility',
      description: 'Multiple independent bays equipped with advanced precision machines for large scale equipment manufacturing',
    },
    {
      icon: Handshake,
      title: 'Turnkey Solutions',
      description: 'From design to delivery, we support you end-to-end',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="relative w-full py-16 sm:py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/Coke_equipment - For Hero Image.png"
            alt="VMIL Manufacturing Facility"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-vmil-navy/80 via-vmil-navy/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold text-white mb-4 sm:mb-6">
              About Velmurugan Industries
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/80">
              Engineering Excellence Since 1979, Now in its Third Generation
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className={`w-full py-16 md:py-24 ${isDark ? 'bg-vmil-navy' : 'bg-vmil-light'}`}>
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Three-Generation Legacy */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center mb-12 md:mb-16">
              <div>
                <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  A Three-Generation Legacy of
                  <span className={`block ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>Precision Engineering</span>
                </h2>
                <div className={`space-y-4 text-sm sm:text-base lg:text-lg leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
                  <p>
                    <strong>Founded in 1979 by Mr. R. Srinivasan</strong> in Tiruchirapalli, Velmurugan Industries Pvt Ltd
                    (VMIL) began as a commitment to precision machining and engineering excellence.
                    What started as a workshop has grown into a name synonymous with quality heavy
                    engineering, known and trusted throughout the region and relied upon by global OEM
                    partners worldwide in over four decades.
                  </p>
                  <p>
                    Under the leadership of <strong>Mr. Sampath Srinivasan</strong>, the second generation, VMIL
                    transformed from a local workshop into a world-class manufacturing facility. Scaling
                    operations to over 200+ workers, expanding to seven production bays with 250-tonne
                    capacity, and earning ISO certifications and customer recognition from industry leaders
                    like SMS Group, FLSmidth, JSW Steel, BHEL, and Siemens Gamesa, Mr. Sampath
                    established VMIL as a globally competitive precision engineering partner, exporting to
                    five countries across Asia, America, Australia and beyond.
                  </p>
                  <p>
                    Now entering its <strong>third generation</strong>, carrying forward the family's commitment to
                    engineering excellence while steering the company towards new innovations and
                    opportunities. This continuity of vision, from founder to second generation to third,
                    ensures that every project we undertake honors our 45-year legacy while embracing the
                    future of manufacturing.
                  </p>
                </div>
              </div>
              <div className={`relative p-4 sm:p-6 rounded-2xl sm:rounded-3xl ${isDark ? 'bg-white/5' : 'bg-white shadow-xl'}`}>
                {/* 4-Image Product Grid */}
                <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                  <div className="relative overflow-hidden rounded-lg sm:rounded-xl group">
                    <img
                      src="/Lance Trolley.jpg"
                      alt="Lance Trolley"
                      className="w-full h-32 sm:h-40 object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs sm:text-sm font-semibold">Lance Trolley</p>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-lg sm:rounded-xl group">
                    <img
                      src="/Vacuum Vessel.jpeg"
                      alt="Vacuum Vessel"
                      className="w-full h-32 sm:h-40 object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs sm:text-sm font-semibold">Vacuum Vessel</p>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-lg sm:rounded-xl group">
                    <img
                      src="/Swivel Elbow.jpg"
                      alt="Swivel Elbow"
                      className="w-full h-32 sm:h-40 object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs sm:text-sm font-semibold">Swivel Elbow</p>
                    </div>
                  </div>
                  <div className="relative overflow-hidden rounded-lg sm:rounded-xl group">
                    <img
                      src="/Upper Cooling hood - End Part.jpg"
                      alt="Cooling Hood"
                      className="w-full h-32 sm:h-40 object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                      <p className="text-white text-xs sm:text-sm font-semibold">Cooling Hood</p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl text-center ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`}>
                    <div className={`text-2xl sm:text-3xl font-extrabold ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>1979</div>
                    <div className={`text-xs sm:text-sm ${isDark ? 'text-white' : 'text-vmil-steel'}`}>Founded</div>
                  </div>
                  <div className={`p-3 sm:p-4 rounded-lg sm:rounded-xl text-center ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'}`}>
                    <div className={`text-2xl sm:text-3xl font-extrabold ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>45+</div>
                    <div className={`text-xs sm:text-sm ${isDark ? 'text-white' : 'text-vmil-steel'}`}>Years Experience</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Leadership Section */}
            <div className="mb-12 md:mb-16">
              <div className="text-center mb-8 sm:mb-12">
                <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border mb-4 sm:mb-6 ${isDark
                  ? 'bg-vmil-blue/10 border-vmil-blue/20'
                  : 'bg-vmil-blue/10 border-vmil-blue/20'
                  }`}>
                  <span className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
                    Leadership
                  </span>
                </div>
                <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  Guiding Our
                  <span className={`block ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>Vision Forward</span>
                </h2>
              </div>

              <div className={`p-4 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-xl'
                }`}>
                <div className="flex flex-col md:flex-row items-start gap-6 md:gap-8">
                  {/* CEO Photo on the left */}
                  <div className="flex-shrink-0 w-full md:w-48 lg:w-56">
                    <img
                      src="/CEO_Photo.png"
                      alt="Mr. Sampath - Managing Director & CEO"
                      className="w-full h-auto rounded-xl sm:rounded-2xl object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-xl sm:text-2xl font-bold mb-1 sm:mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                      Mr. Sampath
                    </h3>
                    <p className={`font-semibold text-sm sm:text-base mb-3 sm:mb-4 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>Managing Director & CEO</p>
                    <p className={`text-sm sm:text-base lg:text-lg leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
                      Under Mr. Sampath's guidance, Velmurugan continues to thrive. He has played a pivotal role
                      in steering the company toward new innovations and expanding its global reach. His leadership
                      is characterized by a deep commitment to technical excellence and continuous improvement,
                      which has been instrumental in maintaining the company's reputation as a trusted partner in the industry.
                    </p>
                    <p className={`text-xs sm:text-sm md:text-base mt-3 sm:mt-4 ${isDark ? 'text-white' : 'text-vmil-steel/80'}`}>
                      He is also a director of Tiruchirappalli Engineering And Technology Cluster, which is registered
                      with the Ministry of Corporate Affairs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Philosophy */}
            <div className="mb-12 md:mb-16">
              <div className={`rounded-2xl sm:rounded-3xl overflow-hidden ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white shadow-xl'
                }`}>
                <img
                  src="/about-facility.png"
                  alt="VMIL Business Philosophy"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Mission & Vision */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-8 mb-12 md:mb-16">
              {/* Mission */}
              <div className={`p-4 sm:p-8 rounded-2xl sm:rounded-3xl ${isDark ? 'bg-vmil-blue/10 border border-vmil-blue/20' : 'bg-vmil-blue/5 border border-vmil-blue/10'
                }`}>
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/20'}`}>
                    <Target className={`w-5 h-5 sm:w-7 sm:h-7 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                  </div>
                  <h3 className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                    Our Mission
                  </h3>
                </div>
                <p className={`text-sm sm:text-base lg:text-lg leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
                  To provide the best value-added products with zero defect by fulfilling customer expectations,
                  maintaining harmony with the environment, economy, society, fulfilling the aspirations of our
                  workforce and relevant interested parties.
                </p>
              </div>

              {/* Vision */}
              <div className={`p-4 sm:p-8 rounded-2xl sm:rounded-3xl ${isDark ? 'bg-vmil-blue/10 border border-vmil-blue/20' : 'bg-vmil-blue/5 border border-vmil-blue/10'
                }`}>
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'
                    }`}>
                    <Eye className={`w-5 h-5 sm:w-7 sm:h-7 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                  </div>
                  <h3 className={`text-lg sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                    Our Vision
                  </h3>
                </div>
                <ul className="space-y-2 sm:space-y-3">
                  {visionGoals.map((goal, index) => (
                    <li key={index} className="flex items-start gap-2 sm:gap-3">
                      <CheckCircle2 className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5 sm:mt-1 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                      <span className={`text-xs sm:text-sm md:text-base leading-relaxed ${isDark ? 'text-white' : 'text-vmil-steel'}`}>
                        {goal}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Infrastructure Link */}
            <div className={`p-6 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl text-center mb-12 md:mb-16 ${isDark
              ? 'bg-gradient-to-r from-vmil-blue/30 to-vmil-blue/10 border border-vmil-blue/20'
              : 'bg-gradient-to-r from-vmil-blue/10 to-vmil-blue/5 border border-vmil-blue/10'
              }`}>
              <h3 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                Explore Our World-Class Infrastructure
              </h3>
              <p className={`text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 ${isDark ? 'text-white/80' : 'text-vmil-steel'}`}>
                Seven production bays with 250-tonne capacity, equipped with advanced CNC machines and precision equipment.
              </p>
              <button
                onClick={() => onNavigate?.('infrastructure')}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${isDark
                  ? 'bg-vmil-blue text-white hover:bg-vmil-blue/80'
                  : 'bg-vmil-blue text-white hover:bg-vmil-blue/90'
                  }`}
              >
                View Infrastructure
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            {/* Why Partner with VMIL */}
            <div className="mb-12 md:mb-16">
              <div className="text-center mb-8 sm:mb-12">
                <div className={`inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border mb-4 sm:mb-6 ${isDark
                  ? 'bg-vmil-blue/10 border-vmil-blue/20'
                  : 'bg-vmil-blue/10 border-vmil-blue/20'
                  }`}>
                  <span className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`}>
                    Partnership
                  </span>
                </div>
                <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                  Why Partner with VMIL?
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                {partnerReasons.map((reason, index) => {
                  const IconComponent = reason.icon;
                  return (
                    <div
                      key={index}
                      className={`p-5 sm:p-6 rounded-2xl transition-all hover:scale-[1.02] ${isDark
                        ? 'bg-white/5 border border-white/10 hover:border-vmil-blue/30'
                        : 'bg-white shadow-lg hover:shadow-xl border border-gray-100'
                        }`}
                    >
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${isDark ? 'bg-vmil-blue/20' : 'bg-vmil-blue/10'
                        }`}>
                        <IconComponent className={`w-6 h-6 ${isDark ? 'text-vmil-blue' : 'text-vmil-blue'}`} />
                      </div>
                      <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-vmil-blue'}`}>
                        {reason.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-white/80' : 'text-vmil-steel'}`}>
                        {reason.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
